import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

const EXPIRING_SOON_HOURS = 48;
const wantedSettings = [
  // { addr: 164, bit_pos: 4, name: "Open Door Warning" },
  { addr: 164, bit_pos: 2, name: "Intrusion Alarm" },
  // { addr: 122, bit_pos: 1, name: "Watchmaker Mode" },
  { addr: 88,  bit_pos: 0, name: "Opening Delay" },
  { addr: 125, bit_pos: 0, name: "Security Mode" },
  { addr: 104, bit_pos: 0, name: "2-Way Authentication" },
  // { addr: 139, bit_pos: 0, name: "One Door Only" },
  // { addr: 155, bit_pos: 0, name: "Validation Mode" },
  { addr: 186, bit_pos: 32, name: "Enhanced Sound Set" }
];

export default function SettingsTable({ result }) {
  const activeSettings = useMemo(() => {
    const map = new Map();
    result.result?.forEach(device => {
      device.scheduled_settings?.forEach(s => {
        const key = `${s.addr}-${s.bit_pos}`;
        map.set(key, s);
      });
    });
    return map;
  }, [result]);

  const toMillis = (v) => {
    if (v === null || v === undefined) return null;
    const n = Number(v);
    if (Number.isNaN(n)) return null;
    return n < 1e12 ? Math.round(n * 1000) : Math.round(n);
  };

  const computeExpiryMs = (setting) => {
    if (!setting) return null;
    const sysTimeRaw = setting.sys_time ?? setting.systemTime ?? setting.system_time ?? setting.systemtime ?? setting.system;
    const sysMs = toMillis(sysTimeRaw);
    const rem = setting.rem_hours ?? setting.remHours ?? setting.remaining_hours;
    const tot = setting.tot_hours ?? setting.totHours ?? setting.totHours;
    const H = 3600 * 1000;

    if (rem != null && !Number.isNaN(Number(rem)) && sysMs) return sysMs + Number(rem) * H;
    if (tot != null && !Number.isNaN(Number(tot)) && sysMs) return sysMs + Number(tot) * H;
    if (rem != null && !Number.isNaN(Number(rem))) return Date.now() + Number(rem) * H;
    if (tot != null && !Number.isNaN(Number(tot))) return Date.now() + Number(tot) * H;
    return null;
  };

  const formatDate = (ms) => {
    if (!ms) return '—';
    try {
      return new Date(ms).toLocaleString('en-GB', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hour12: false
      });
    } catch (e) {
      return String(ms);
    }
  };

  const now = Date.now();

  return (
    <div className="w-full h-full overflow-auto p-3">
      <table className="min-w-full border border-stone-200 rounded-lg bg-stone-50">
        <thead className="bg-stone-100 sticky top-0 z-10">
          <tr>
            <th className="px-3 py-2 text-left text-sm font-semibold text-stone-700">Setting</th>
            <th className="px-3 py-2 text-center text-sm font-semibold text-stone-700">Status</th>
            <th className="px-3 py-2 text-center text-sm font-semibold text-stone-700">Expires</th>
          </tr>
        </thead>
        <tbody>
          {wantedSettings.map(setting => {
            const key = `${setting.addr}-${setting.bit_pos}`;
            const active = activeSettings.get(key);
            const expiryMs = computeExpiryMs(active);

            let statusLabel = 'Not Active';
            let statusClass = 'text-stone-500 italic';
            let expiryDisplay = '—';

            if (expiryMs) {
              expiryDisplay = formatDate(expiryMs);
              if (now >= expiryMs) {
                statusLabel = 'Expired';
                statusClass = 'text-red-600 font-medium';
              } else {
                const remainingHours = (expiryMs - now) / (3600 * 1000);
                if (remainingHours <= EXPIRING_SOON_HOURS) {
                  statusLabel = `Expiring soon (${Math.round(remainingHours)}h)`;
                  statusClass = 'text-amber-600 font-medium';
                } else {
                  statusLabel = `Active (${Math.round(remainingHours)}h left)`;
                  statusClass = 'text-green-700 font-medium';
                }
              }
            }

            return (
              <tr key={key} className="hover:bg-stone-100">
                <td className="px-3 py-2 text-sm font-medium text-stone-700">{setting.name}</td>
                <td className={`px-3 py-2 text-sm text-center ${statusClass}`}>{statusLabel}</td>
                <td className="px-3 py-2 text-sm text-center text-stone-700">{expiryDisplay}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

SettingsTable.propTypes = {
  result: PropTypes.array.isRequired,
};